import i18n from '@/i18n'

/**
 * Org kill switches return a plain 403. Axios then sets `error.message` to
 * "Request failed with status code 403", which is what a banner shows if a
 * caller prints that field. Only those kill-switch sentences are rewritten
 * here. Plan gates ("Your plan does not include…", "Please upgrade") and
 * trial caps stay on their own upgrade path.
 */
export type BlockedFeature =
  | 'twinChat'
  | 'studyCreation'
  | 'assistant'
  | 'intelligentMapping'
  | 'marketplacePublish'

const KILL_SWITCH_MESSAGES: Array<{ kind: BlockedFeature; phrase: string }> = [
  { kind: 'twinChat', phrase: 'twin chat is not enabled for this organization' },
  { kind: 'studyCreation', phrase: 'study creation is turned off for this organization' },
  { kind: 'assistant', phrase: 'assistant platform is not enabled' },
  { kind: 'intelligentMapping', phrase: 'intelligent mapping is disabled for this organization' },
  { kind: 'marketplacePublish', phrase: 'publishing to marketplace is not enabled for your organization' },
]

interface AxiosLikeError {
  response?: {
    status?: number
    data?: { message?: string | Array<string> }
  }
}

function readServerMessage(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return ''
  }
  const raw = (error as AxiosLikeError).response?.data?.message
  if (Array.isArray(raw)) {
    return raw.filter((item) => typeof item === 'string').join(' ')
  }
  if (typeof raw === 'string') {
    return raw
  }
  return ''
}

export function detectBlockedFeatureError(error: unknown): BlockedFeature | null {
  if (!error || typeof error !== 'object') {
    return null
  }
  if ((error as AxiosLikeError).response?.status !== 403) {
    return null
  }
  const message = readServerMessage(error).toLowerCase()
  const match = KILL_SWITCH_MESSAGES.find((entry) => message.includes(entry.phrase))
  return match?.kind ?? null
}

const COPY_KEY = {
  twinChat: 'blockedFeature.twinChat',
  studyCreation: 'blockedFeature.studyCreation',
  assistant: 'blockedFeature.assistant',
  intelligentMapping: 'blockedFeature.intelligentMapping',
  marketplacePublish: 'blockedFeature.marketplacePublish',
} as const satisfies Record<BlockedFeature, string>

/** Contact-support copy for a kill-switch 403, or null for every other error. */
export function blockedFeatureMessage(error: unknown): string | null {
  const kind = detectBlockedFeatureError(error)
  if (!kind) {
    return null
  }
  return i18n.t(COPY_KEY[kind], { ns: 'app' })
}
